import React, { useState } from 'react';
import { CustomModal, Select, theme, Input } from '@jadwalh/ui';
import { useIntl } from 'react-intl';
import { Button, Flex } from '@mantine/core';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  Controller,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import instance from '../../../axios-instance';
import { setErrors } from '@jadwalh/utils';
import { addWorkflow } from '../../../api/workflow.api';
import { Container } from './styles';
import { toast } from 'react-toastify';
import MultiSelectDropdown from '../components/MultiSelectDropdownWithPortal';
import { getProfile } from '../../../api/profile.api';
import { usePreventKeyboardScroll } from '../../../hooks/usePreventKeyboardScroll';
import { useSelector } from 'react-redux';
import { getProfileData } from '../../../store/profile.slice';

interface FormInitialValues {
  name: string;
  event_ids: string[] | undefined;
}
const AddWorkflowModal = ({ opened, setOpened }: any) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const profile = useSelector(getProfileData);
  const methods = useForm<FormInitialValues>({
    defaultValues: {
      name: '',
      event_ids: undefined,
    },
  });

  // Use the custom hook to prevent keyboard scroll issues
  usePreventKeyboardScroll(opened);

  const { data: events } = useQuery({
    queryKey: ['events'],
    queryFn: () => instance.get(`app/list/events?client_id=${profile.id}&status=1&scope=general&category=event`),
    select: (response: any) => response.data.data,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const [selectedEvents, setSelectedEvents] = useState<string[] | undefined>();
  
  const handleEventsChange = (selectedIds: string[]) => {
    setSelectedEvents(selectedIds);
    setValue('event_ids', selectedIds || []);
    clearErrors('event_ids');
  };

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    setError,
    formState: { errors },
    clearErrors,
  } = methods;

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data: FormInitialValues) => {
      return addWorkflow(data);
    },
    onSuccess: (newData) => {
      setOpened(false);
      getProfile();

      toast.success(formatMessage({ id: 'newAppointmentAdded' }));
      navigate(`/workflows/update/${newData.id}`);
    },
    onError: (error: any) => {
      const errors = error.response.data.errors;
      setErrors(errors, setError);
    },
  });

  const onSubmit: SubmitHandler<FormInitialValues> = async (data: any) => {
    await mutateAsync(data);
  };

  return (
    <CustomModal
      opened={opened}
      onClose={() => {
        setOpened(false);
        setSelectedEvents([]);
      }}
      withCloseButton={false}
      closeOnEscape={false}
      header={formatMessage({ id: 'addNewWorkflow' })}
      className="add-route"
      size="529px"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Container>
            <div className="popup-body">
              <Flex direction="column" justify={'flex-start'} gap="16px">
                <Input
                  label={formatMessage({ id: 'workflowName' })}
                  name="name"
                  maxLength={55}
                  registerOptions={{
                    required: {
                      value: true,
                      message: formatMessage({
                        id: 'requiredField',
                      }),
                    },
                  }}
                />
                <Flex direction="column" gap="4px">
                  <p className="event-type-label">
                    {formatMessage({ id: 'workflowEvents' })}
                  </p>
                  <MultiSelectDropdown
                    items={
                      events?.map((event: any) => ({
                        id: String(event.value),
                        name: String(event.label),
                      })) || []
                    }
                    classes={`events-container-select-wrapper`}
                    selectedItems={selectedEvents || []}
                    onSelectionChange={handleEventsChange}
                    placeholder= {formatMessage({ id: 'chooseEvents' })}
                    searchPlaceholder="البحث عن حدث"
                    selectAllLabel="تحديد الكل"
                    emptyLabel="لا توجد أحداث"
                    position="below"
                    usePortal={true}
                    btnClass={errors.event_ids ? 'hasError' : ''}
                    showSearch={false}
                  />
                  <Input
                    name="event_ids"
                    type="hidden"
                    value={selectedEvents?.join(',') || ''}
                    registerOptions={{
                      required: {
                        value: true,
                        message: formatMessage({
                          id: 'requiredField',
                        }),
                      },
                    }}
                  />
                </Flex>
                <div />
              </Flex>
            </div>
            <div className="popup-footer">
              <Flex justify={'flex-start'} w="100%" gap="16px">
                <Button
                  className="add"
                  type="submit"
                  style={{
                    backgroundColor: '#242424',
                    borderRadius: '8px',
                    color: theme.colours.white,
                    height: '32px',
                    width: '80px',
                    minWidth: '80px',
                  }}
                  loading={isLoading}
                >
                  {!isLoading && formatMessage({ id: 'add' })}
                </Button>
                <Button
                  className={'cancel'}
                  onClick={() => setOpened(false)}
                  style={{
                    backgroundColor: theme.colours.white,
                    borderRadius: '8px',
                    color: theme.colours.blackText,
                    border: `1px solid ${theme.colours.newOutline}`,
                    height: '32px',
                    width: '80px',
                    minWidth: '80px',
                  }}
                >
                  {formatMessage({ id: 'cancel' })}
                </Button>
              </Flex>
            </div>
          </Container>
        </form>
      </FormProvider>
    </CustomModal>
  );
};

export default AddWorkflowModal;
